import { identity, omit, pickBy } from 'lodash-es'
import type { StoryblokRichTextNode, StoryblokRichTextNodeResolver } from '@storyblok/richtext'
import type { VNode } from 'vue'
import { defu } from 'defu'
import type { UExpansionPanelStoryblok } from '#core/storyblok-component-types'
import { createRichTextResolver } from '#layers/storyblok/app/utils/resolver-utils'

type ComponentResolvers = Record<string, StoryblokRichTextNodeResolver<VNode>>

export const UBtnResolver = createRichTextResolver(({ fields, component }) => {
  const { getLink } = useLinkType()
  const props = pickBy(omit(fields.url, ['linktype', 'cached_url', 'fieldtype', 'url']), identity)

  return h(resolveComponent(component), {
    ...getLink(fields.url).props,
    ...props,
    text: fields.text,
    color: fields.color,
    variant: fields.variant,
    pro: fields.pro,
    inverted: fields.inverted,
    size: fields.size,
    prependIcon: fields.prependIcon,
    appendIcon: fields.appendIcon,
  })
})

export const UExpansionPanelResolver = createRichTextResolver(({ fields, component }) => {
  const { render } = useRenderer()

  return h('div', {
    class: '[&:not(:last-child)]:mb-6',
  }, [
    h(resolveComponent(component), null, {
      default: () => fields.body.map((panel: UExpansionPanelStoryblok) => h(resolveComponent(panel.component), {
        title: panel.title,
      }, () => render(panel.description))),
    }),
  ])
})

export const UTableResolver = createRichTextResolver(({ fields, component }) => {
  function parseCSVToStructuredObject(fileContent: string) {
    const rows = fileContent.split('\n').filter(Boolean).map(row => row.split('\t'))
    const structuredObject = {
      thead: [] as Record<never, string>[],
      tbody: [] as Record<never, string>[],
    }

    if (rows.length > 0) {
      structuredObject.thead = rows[0].map(header => ({ value: header }))
    }

    for (let i = 1; i < rows.length; i++) {
      const row = { body: rows[i].map(cell => ({ value: cell })) }
      structuredObject.tbody.push(row)
    }

    return structuredObject
  }

  const content = parseCSVToStructuredObject(fields.pasteContent)

  return h(resolveComponent(component), {
    content: fields.pasteContent ? content : fields.content,
    description: fields.description,
    hideHeader: fields?.hideHeader,
    isVertical: fields?.isVertical,
    class: '[&:not(:last-child)]:mb-6',
  })
})

const defaultComponents: ComponentResolvers = {
  uBtn: UBtnResolver(),
  UExpansionPanels: UExpansionPanelResolver(),
  uTable: UTableResolver(),
}

export function renderComponentNode(node: StoryblokRichTextNode<VNode>, componentResolvers?: Record<string, StoryblokRichTextNodeResolver<VNode>>) {
  const components: ReturnType<typeof h>[] = []
  const resolvers = defu(componentResolvers, defaultComponents)

  for (const body of node?.attrs?.body) {
    const { component, _uid, ...fields } = body
    const resolver = resolvers[component]

    if (resolver) {
      components.push(
        resolver({ id: node.attrs?.id, component, _uid, fields }),
      )
    }
    else {
      components.push(
        h('div', `fallback: component ${component} is not handled`),
      )
    }
  }

  return components
}
